import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    LayoutDashboard,
    Package,
    FileText,
    Users,
    LogOut,
    Menu,
    X,
    ChevronRight,
    Settings as SettingsIcon,
    Beaker,
    ScrollText,
    CreditCard,
    Link as LinkIcon,
    Image,
    Webhook,
    Shield,
    MousePointerClick,
    Target,
} from 'lucide-react';

interface DashboardLayoutProps {
    children: React.ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
    const { user, signOut, isAdmin, hasPermission } = useAuth();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const affiliateNavItems = [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/assets', label: 'Products & Links', icon: LinkIcon },
        { path: '/creatives', label: 'Creatives', icon: Image },
        { path: '/postbacks', label: 'Postbacks', icon: Webhook },
        { path: '/billing', label: 'Billing', icon: CreditCard },
    ];

    const adminNavItems = [
        { path: '/admin', label: 'Overview', icon: LayoutDashboard, permission: 'view_analytics' },
        { path: '/admin/invoices', label: 'Invoices', icon: FileText, permission: 'manage_finance' },
        { path: '/admin/users', label: 'Affiliates', icon: Users, permission: 'manage_users' },
        { path: '/admin/team', label: 'Team', icon: Shield, permission: 'manage_users' },
        { path: '/admin/products', label: 'Products', icon: Package, permission: 'manage_settings' },
        { path: '/admin/sandbox', label: 'Test Sandbox', icon: Beaker, permission: 'manage_settings' },
        { path: '/admin/clicks', label: 'Clicks', icon: MousePointerClick, permission: 'view_analytics' },
        { path: '/admin/conversions', label: 'Conversions', icon: Target, permission: 'view_analytics' },
        { path: '/admin/postback-logs', label: 'Postback Logs', icon: ScrollText, permission: 'view_analytics' },
        { path: '/admin/global-postbacks', label: 'Global Postbacks', icon: Webhook, permission: 'manage_settings' },
    ];

    const navItems = isAdmin
        ? adminNavItems.filter(item => !item.permission || hasPermission(item.permission))
        : affiliateNavItems;

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Sign out error:', error);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:static inset-y-0 left-0 z-50
                w-64 glass-dark transform transition-transform duration-300
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="flex flex-col h-full">
                    {/* Logo Area */}
                    <div className="flex items-center justify-between p-6 border-b border-white/10">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-linear-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-glow">
                                <span className="text-white font-bold text-xl">A</span>
                            </div>
                            <div className="flex flex-col">
                                <h1 className="font-bold text-white text-lg leading-tight">Affiliate</h1>
                                <p className="text-[10px] uppercase tracking-widest text-primary-400 font-semibold">Platform</p>
                            </div>
                        </div>
                        <button
                            className="lg:hidden text-slate-400 hover:text-white"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-1">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`sidebar-link ${isActive ? 'active' : ''}`}
                                    onClick={() => setSidebarOpen(false)}
                                >
                                    <Icon size={20} />
                                    <span>{item.label}</span>
                                    {isActive && <ChevronRight size={16} className="ml-auto" />}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* User section */}
                    <div className="p-4 border-t border-white/10 mt-auto">
                        <div className="flex items-center gap-3 mb-4 px-4 py-2 rounded-xl bg-white/5 border border-white/5">
                            <div className="w-10 h-10 rounded-full bg-linear-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-sm">
                                <span className="text-white font-bold">
                                    {user?.email?.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-white truncate">
                                    {user?.companyName || user?.email?.split('@')[0]}
                                </p>
                                <p className="text-[10px] text-slate-400 uppercase tracking-tighter">{user?.role}</p>
                            </div>
                        </div>
                        <Link
                            to="/settings"
                            className="sidebar-link w-full text-slate-400 hover:text-white mb-2"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <SettingsIcon size={18} />
                            <span className="text-sm font-medium">Settings</span>
                        </Link>
                        <button
                            onClick={handleSignOut}
                            className="sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                            <LogOut size={18} />
                            <span className="text-sm font-medium">Sign Out</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 flex flex-col min-h-screen">
                {/* Top bar */}
                <header className="h-[72px] glass border-b border-white/10 flex items-center justify-between px-8 sticky top-0 z-40">
                    <button
                        className="lg:hidden text-slate-400 hover:text-white"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <Menu size={24} />
                    </button>

                    <div className="flex items-center gap-4 ml-auto">
                        <span className={`badge ${isAdmin ? 'badge-info' : 'badge-success'} shadow-sm`}>
                            {isAdmin ? 'Admin' : 'Affiliate'}
                        </span>
                    </div>
                </header>

                {/* Page content */}
                <div className="flex-1 p-6 overflow-auto">
                    {children}
                </div>
            </main>
        </div>
    );
}
